import React, { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
import * as Sentry from "@sentry/react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { BsPeople, BsPersonCheck, BsGraphUpArrow, BsBullseye, BsAward, BsBuilding, BsExclamationTriangle, BsMegaphone } from "react-icons/bs";
import analyticsDashboardApi from "../../../../services/analyticsDashboard.service";
import { formatNumber, formatPercentage } from "../../../../utils/analyticsDashboardFormat";
import {
  WidgetSection,
  MetricsGrid,
  MetricCard,
  MetricCardHeader,
  MetricLabel,
  MetricIconWrap,
  MetricValue,
  ChartsGrid,
  ChartCard,
  ChartCardTitle,
  SkeletonBlock,
  EmptyStateBox,
  InfoNote,
  MetricTooltip,
  SectionDivider,
  SectionDividerIcon,
  SectionDividerText,
  SectionDividerTitle,
  SectionDividerDesc,
} from "./Widgets.styles";

// Modern, accessible color palette for pie/bar charts
const COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#3b82f6", "#8b5cf6"];

const tooltipStyle = {
  contentStyle: {
    background: "white",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    boxShadow: "0 6px 18px rgba(15,23,42,0.10)",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.82rem",
  },
  labelStyle: { fontWeight: 700, color: "#111827", marginBottom: 4 },
  itemStyle: { color: "#4B5563" },
};

// Timezone offset in minutes (e.g. -360 for UTC-6 / Mexico City).
const LOCAL_UTC_OFFSET_MIN = -new Date().getTimezoneOffset();

const ClientAnalyticsWidgets = ({ companyId, startDate, endDate, channelIds, dataEnrichment }) => {
  // NOTE: the `/analytics/clickhouse/client-analytics` REST endpoint only
  // accepts a single `channelId` today (backend limitation, tracked as a
  // follow-up in PR #168). `getSourceDetails` (below) does support an array
  // of `channelIds`, but we still pass it the same single channel here so
  // both queries stay consistent with each other until client-analytics
  // also supports multi-channel filtering.
  const channelId = channelIds?.length ? channelIds[0] : undefined;
  const hasMultipleChannelsSelected = (channelIds?.length || 0) > 1;

  const {
    data: clientAnalytics,
    isLoading: clientLoading,
    error: clientError,
  } = useQuery(
    ["clickhouse-client-analytics", companyId, startDate, endDate, channelId],
    () =>
      analyticsDashboardApi.getClickHouseClientAnalytics({
        companyId,
        startDate,
        endDate,
        channelId,
        timezoneOffset: LOCAL_UTC_OFFSET_MIN,
        limit: 10000,
      }),
    { enabled: !!companyId }
  );

  const {
    data: sourceDetails,
    isLoading: sourceDetailsLoading,
    error: sourceDetailsError,
  } = useQuery(
    ["clickhouse-client-source-details", companyId, startDate, endDate, channelId],
    () =>
      analyticsDashboardApi.getSourceDetails({
        companyId,
        startDate,
        endDate,
        channelIds: channelId ? [channelId] : undefined,
        timezoneOffset: LOCAL_UTC_OFFSET_MIN,
      }),
    { enabled: !!companyId }
  );

  const isLoading = clientLoading || sourceDetailsLoading;
  const error = clientError || sourceDetailsError;

  const clientTotals = useMemo(() => {
    if (!clientAnalytics?.data) return null;
    return clientAnalytics.data.reduce(
      (acc, item) => ({
        totalClients: acc.totalClients + (item.total_clients || 0),
        finishedClients: acc.finishedClients + (item.finished_clients || 0),
        fbFormClients: acc.fbFormClients + (item.fb_form_clients || 0),
        fbAdsClients: acc.fbAdsClients + (item.fb_ads_clients || 0),
        organicClients: acc.organicClients + (item.organic_clients || 0),
        supportFinished: acc.supportFinished + (item.support_finished_clients || 0),
        salesFinished: acc.salesFinished + (item.sales_finished_clients || 0),
      }),
      {
        totalClients: 0,
        finishedClients: 0,
        fbFormClients: 0,
        fbAdsClients: 0,
        organicClients: 0,
        supportFinished: 0,
        salesFinished: 0,
      }
    );
  }, [clientAnalytics]);

  const sourceBreakdownData = useMemo(() => {
    if (!clientTotals) return [];
    const items = [
      { name: "Facebook Forms", value: clientTotals.fbFormClients },
      { name: "Facebook Ads", value: clientTotals.fbAdsClients },
      { name: "Organic", value: clientTotals.organicClients },
    ].filter((item) => item.value > 0);
    const total = items.reduce((sum, item) => sum + item.value, 0);
    return items.map((item) => ({ ...item, percentage: total > 0 ? (item.value / total) * 100 : 0 }));
  }, [clientTotals]);

  const funnelData = useMemo(() => {
    if (!clientTotals) return [];
    return [
      { name: "Total Conversaciones", value: clientTotals.totalClients },
      { name: "Conversaciones finalizadas", value: clientTotals.finishedClients },
      { name: "Soporte", value: clientTotals.supportFinished },
      { name: "Ventas", value: clientTotals.salesFinished },
    ].filter((item) => item.value > 0);
  }, [clientTotals]);

  const topSourcesData = useMemo(() => {
    if (!sourceDetails?.data) return [];
    return sourceDetails.data
      .map((item) => {
        let name = "";
        if (item.source === "FB_FORM" && item.form_id) {
          const formName = dataEnrichment?.getFormName(item.form_id);
          name = formName && formName !== item.form_id ? formName : `Facebook Form ${item.form_id.slice(-6)}`;
        } else if (item.source === "FB_ADS" && item.source_id) {
          name = `Facebook Ad ${item.source_id.slice(-6)}`;
        } else if (item.source === "ORGANIC") {
          name = "Organic Traffic";
        } else {
          name = `${item.source} ${item.source_id || item.form_id || ""}`.trim();
        }
        // Coerce to number — the API may return client_count as a string
        return { name, clients: Number(item.client_count) || 0 };
      })
      .filter((item) => item.clients > 0);
  }, [sourceDetails, dataEnrichment]);

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: { component: "ClientAnalyticsWidgets", op: "fetchClientAnalytics" },
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <MetricsGrid columns={4}>
        {[...Array(4)].map((_, i) => (
          <MetricCard key={i}>
            <SkeletonBlock style={{ height: 16, width: "50%", marginBottom: 12 }} />
            <SkeletonBlock style={{ height: 48 }} />
          </MetricCard>
        ))}
      </MetricsGrid>
    );
  }

  if (error) {
    return (
      <EmptyStateBox>
        <BsExclamationTriangle size={32} style={{ color: "#DC2626", marginBottom: 12 }} />
        <div>Error al cargar análisis de conversaciones</div>
      </EmptyStateBox>
    );
  }

  return (
    <WidgetSection>
      {hasMultipleChannelsSelected && (
        <InfoNote>
          Hay {channelIds.length} canales seleccionados, pero este análisis solo admite un canal a la vez. Se
          está mostrando únicamente el canal <strong>{dataEnrichment?.getChannelName(channelId) || channelId}</strong>.
        </InfoNote>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SECCIÓN 1 — MÉTRICAS DE CONVERSACIONES
          Volumen operativo: cantidad, orígenes y distribución de fuentes.
         ══════════════════════════════════════════════════════════════════ */}
      <SectionDivider>
        <SectionDividerIcon bg="#6366f1">
          <BsPeople />
        </SectionDividerIcon>
        <SectionDividerText>
          <SectionDividerTitle>Métricas de Conversaciones</SectionDividerTitle>
          <SectionDividerDesc>Volumen operativo: cantidad de conversaciones, orígenes y distribución de fuentes</SectionDividerDesc>
        </SectionDividerText>
      </SectionDivider>

      <MetricsGrid columns={3}>
        <MetricCard accentColor="#6366f1">
          <MetricCardHeader>
            <MetricLabel>
              Total Conversaciones
              <MetricTooltip text="Total de conversaciones únicas iniciadas en el período seleccionado, independientemente de su estado final." />
            </MetricLabel>
            <MetricIconWrap bg="#EEF2FF" color="#6366f1"><BsPeople /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(clientTotals?.totalClients || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#10b981">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Orgánicas
              <MetricTooltip text="Conversaciones iniciadas sin campaña ni formulario de Facebook; representan tráfico directo o manual." />
            </MetricLabel>
            <MetricIconWrap bg="#F0FDF4" color="#10b981"><BsGraphUpArrow /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(clientTotals?.organicClients || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#8b5cf6">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Pagadas
              <MetricTooltip text="Conversaciones originadas por campañas de pago (Facebook Ads + Facebook Forms). Calculado como: Total − Orgánicas." />
            </MetricLabel>
            <MetricIconWrap bg="#F5F3FF" color="#8b5cf6"><BsMegaphone /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>
            {formatNumber(Math.max(0, (clientTotals?.totalClients || 0) - (clientTotals?.organicClients || 0)))}
          </MetricValue>
        </MetricCard>
      </MetricsGrid>

      <ChartsGrid columns={2}>
        <ChartCard>
          <ChartCardTitle>
            <BsBuilding /> Desglose de Fuentes de Conversaciones
            <MetricTooltip text="Distribución de conversaciones según su canal de origen: formularios de Facebook, anuncios o tráfico orgánico." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sourceBreakdownData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${formatPercentage((percent || 0) * 100)}`}
                outerRadius={90}
                innerRadius={40}
                paddingAngle={3}
                dataKey="value"
              >
                {sourceBreakdownData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [formatNumber(value), "Conversaciones"]} {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard>
          <ChartCardTitle>
            <BsAward /> Origen de Conversaciones
            <MetricTooltip text="Volumen de conversaciones agrupadas por fuente de origen específica (formulario, anuncio o campaña). Solo se muestran fuentes con al menos una conversación." />
          </ChartCardTitle>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart width={400} height={300}>
              <Pie
                data={topSourcesData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={110}
                paddingAngle={3}
                dataKey="clients"
                nameKey="name"
                label={({ name, percent }) => percent > 0.04 ? `${name} ${(percent * 100).toFixed(0)}%` : ""}
                labelLine={false}
              >
                {topSourcesData.map((_, index) => (
                  <Cell key={`source-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [formatNumber(value), "Conversaciones"]} {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: "0.8rem", paddingTop: 8 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>

      {/* ══════════════════════════════════════════════════════════════════
          SECCIÓN 2 — MÉTRICAS DE CONVERSIÓN
          Éxito comercial: tasa de cierre, embudo de ventas y soporte.
         ══════════════════════════════════════════════════════════════════ */}
      <SectionDivider>
        <SectionDividerIcon bg="#f59e0b">
          <BsBullseye />
        </SectionDividerIcon>
        <SectionDividerText>
          <SectionDividerTitle>Métricas de Conversión</SectionDividerTitle>
          <SectionDividerDesc>Éxito comercial: conversaciones cerradas, tasa de conversión y embudo de ventas/soporte</SectionDividerDesc>
        </SectionDividerText>
      </SectionDivider>

      <MetricsGrid columns={2}>
        <MetricCard accentColor="#3b82f6">
          <MetricCardHeader>
            <MetricLabel>
              Conversaciones Cerradas
              <MetricTooltip text="Conversaciones marcadas como finalizadas por un agente o bot durante el período seleccionado." />
            </MetricLabel>
            <MetricIconWrap bg="#EFF6FF" color="#3b82f6"><BsPersonCheck /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>{formatNumber(clientTotals?.finishedClients || 0)}</MetricValue>
        </MetricCard>
        <MetricCard accentColor="#f59e0b">
          <MetricCardHeader>
            <MetricLabel>
              Tasa de Conversión
              <MetricTooltip text="Porcentaje de conversaciones finalizadas respecto al total: (Cerradas / Total) × 100." />
            </MetricLabel>
            <MetricIconWrap bg="#FFFBEB" color="#f59e0b"><BsBullseye /></MetricIconWrap>
          </MetricCardHeader>
          <MetricValue>
            {clientTotals?.totalClients > 0
              ? formatPercentage((clientTotals.finishedClients / clientTotals.totalClients) * 100)
              : "0%"}
          </MetricValue>
        </MetricCard>
      </MetricsGrid>

      <ChartCard>
        <ChartCardTitle>
          <BsBullseye /> Embudo de Conversaciones
          <MetricTooltip text="Comparación entre el total de conversaciones y las que avanzaron a las etapas de cierre (soporte y ventas)." />
        </ChartCardTitle>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={funnelData} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }} barCategoryGap="35%">
            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" width={145} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(value) => [formatNumber(value), "Conversaciones"]} {...tooltipStyle} />
            <Bar dataKey="value" fill="#6366f1" radius={[0, 4, 4, 0]}>
              {funnelData.map((_, index) => (
                <Cell key={`funnel-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </WidgetSection>
  );
};

export default ClientAnalyticsWidgets;
